import { useState, useEffect, useMemo, useRef } from "react";
import { Modal, Button, Input, Select } from "@legal-500/legal500-design-system";
import type { CreateSurveyBody, Survey, SurveyEditionFields } from "@/api/surveys.service";
import { newAxisEditionOptions } from "@/api/surveyTaxonomy.service";
import { ErrorBanner } from "@/components/ErrorBanner/ErrorBanner";
import { useFeatureFlags, useFeatureFlagsLoadFailure } from "@/hooks/useFeatureFlags";
import { useNewAxisEditions } from "@/hooks/useNewAxisEditions";
import { usePublications } from "@/hooks/usePublications";
import { NEW_TAXONOMY_FLAG } from "@/hooks/useTaxonomyMode";
import { useSupportedLanguages } from "@/hooks/useSupportedLanguages";
import type { LegacyPublication } from "@/api/legacy-publications.service";
import type { TaxonomyMode } from "@/api/submissions.service";
import {
  DuplicateSurveyWarning,
  EMPTY_SURVEY_CONDITIONS,
  ExceptionConditionsFields,
  hasExceptionConditions,
  surveyConditionsToPayloadFields,
  toSurveyConditionValues,
  type SurveyConditionValues,
} from "../surveyConditions";
import { useDuplicateSurveyWarning } from "../useDuplicateSurveyWarning";
import styles from "./SurveyManagement.module.css";

export type SurveyFormInitialValues = Partial<
  Pick<
    Survey,
    | "name"
    | "edition"
    | "publication"
    | "publicationId"
    | "editionId"
    | "defaultLanguage"
    | "definition"
    | "randomQuestionCount"
    | "regionTypes"
    | "refereeTypes"
    | "refereeCategories"
  >
>;

type SurveyFormModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CreateSurveyBody) => Promise<void>;
  title?: string;
  submitLabel?: string;
  showNameField?: boolean;
  initialValues?: SurveyFormInitialValues;
  taxonomy?: TaxonomyMode;
};

function SurveyFormModal({
  isOpen,
  onClose,
  onSave,
  title = "Create Survey",
  submitLabel = "Create",
  showNameField = true,
  initialValues,
  taxonomy,
}: SurveyFormModalProps) {
  const currentYear = new Date().getFullYear();
  const [name, setName] = useState("");
  const [editionYear, setEditionYear] = useState("");
  const [selectedPublicationId, setSelectedPublicationId] = useState("");
  const [selectedEditionId, setSelectedEditionId] = useState("");
  const [defaultLanguage, setDefaultLanguage] = useState("en");
  const [conditions, setConditions] = useState<SurveyConditionValues>(EMPTY_SURVEY_CONDITIONS);
  const [conditionsExpanded, setConditionsExpanded] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const wasOpenRef = useRef(false);
  const prefilledEditionRef = useRef(false);

  const { hasFlag, isLoading: flagsLoading } = useFeatureFlags();
  const { failed: flagsFailed, retry: retryFlags } = useFeatureFlagsLoadFailure();
  const flagsBlocked = flagsLoading || flagsFailed;
  const newTaxonomyEnabled = hasFlag(NEW_TAXONOMY_FLAG);
  const newAxis = taxonomy ? taxonomy === "new" : newTaxonomyEnabled;

  const { allLanguages } = useSupportedLanguages();

  const editionOptions = useMemo(() => {
    const years: { value: string; label: string }[] = [];
    for (let year = currentYear + 1; year >= 2017; year--) {
      years.push({ value: String(year), label: String(year) });
    }
    return years;
  }, [currentYear]);

  const { data: publicationsData } = usePublications(
    { edition: editionYear || undefined },
    { enabled: !flagsBlocked && !newAxis && !!editionYear },
  );

  const { data: newAxisEditions, error: newAxisEditionsError } = useNewAxisEditions({
    enabled: isOpen && !flagsBlocked && newAxis,
  });

  const editionSelectOptions = useMemo(
    () => [{ value: "", label: "Select Edition" }, ...newAxisEditionOptions(newAxisEditions)],
    [newAxisEditions],
  );

  const selectedNewAxisEdition = useMemo(
    () => newAxisEditions?.find(edition => edition.editionId === selectedEditionId) ?? null,
    [newAxisEditions, selectedEditionId],
  );

  const publicationOptions = useMemo(() => {
    const pubs = publicationsData?.publications ?? [];
    return [
      { value: "", label: "Select Publication" },
      ...pubs.map((p: LegacyPublication) => ({
        value: String(p.id),
        label: p.publication ? `${p.publication} (${p.edition ?? "n/a"})` : `Publication ${p.id}`,
      })),
    ];
  }, [publicationsData?.publications]);

  const selectedPublication = useMemo(() => {
    if (!selectedPublicationId || !publicationsData?.publications) return null;
    return (
      publicationsData.publications.find(
        (p: LegacyPublication) => String(p.id) === selectedPublicationId,
      ) ?? null
    );
  }, [selectedPublicationId, publicationsData?.publications]);

  // A collapsed section means a general survey: both the duplicate warning and
  // the create payload treat conditions as empty while collapsed.
  const effectiveConditions = conditionsExpanded ? conditions : EMPTY_SURVEY_CONDITIONS;

  const duplicateSurvey = useDuplicateSurveyWarning({
    enabled: isOpen && !flagsBlocked,
    newTaxonomyEnabled,
    ...(newAxis
      ? {
          editionId: selectedNewAxisEdition?.editionId,
          publicationId: selectedNewAxisEdition?.publicationId ?? undefined,
        }
      : {
          edition: selectedPublication?.edition,
          publicationId: selectedPublicationId ? Number(selectedPublicationId) : undefined,
        }),
    conditions: effectiveConditions,
  });

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setName(initialValues?.name ?? "");
      const initialYear = initialValues?.edition?.match(/\d{4}/)?.[0] ?? String(currentYear);
      setEditionYear(initialYear);
      setSelectedPublicationId(
        initialValues?.publicationId ? String(initialValues.publicationId) : "",
      );
      setSelectedEditionId(initialValues?.editionId ?? "");
      prefilledEditionRef.current = Boolean(initialValues?.editionId);
      setDefaultLanguage(initialValues?.defaultLanguage ?? "en");
      setConditions(toSurveyConditionValues(initialValues));
      setConditionsExpanded(hasExceptionConditions(initialValues));
      setErrors({});
    }
    wasOpenRef.current = isOpen;
  }, [
    isOpen,
    currentYear,
    initialValues?.name,
    initialValues?.edition,
    initialValues?.publicationId,
    initialValues?.editionId,
    initialValues?.defaultLanguage,
    initialValues?.regionTypes,
    initialValues?.refereeTypes,
    initialValues?.refereeCategories,
  ]);

  useEffect(() => {
    if (!isOpen || prefilledEditionRef.current || !newAxisEditions) return;
    prefilledEditionRef.current = true;
    const legacyMatch = initialValues?.publicationId
      ? newAxisEditions.find(edition => edition.publicationId === initialValues.publicationId)
      : undefined;
    if (legacyMatch) setSelectedEditionId(legacyMatch.editionId);
  }, [isOpen, newAxisEditions, initialValues?.publicationId]);

  const handleNewAxisEditionChange = (value: string) => {
    setSelectedEditionId(value);
    clearError("edition");
  };

  const handleEditionChange = (value: string) => {
    setEditionYear(value);
    setSelectedPublicationId("");
    clearError("edition");
  };

  const handlePublicationChange = (value: string) => {
    setSelectedPublicationId(value);
    clearError("publication");
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (showNameField && !name.trim()) newErrors.name = "Survey name is required";
    if (newAxis) {
      if (!selectedNewAxisEdition) newErrors.edition = "Edition is required";
    } else {
      if (!editionYear) newErrors.edition = "Edition is required";
      if (!selectedPublicationId) newErrors.publication = "Publication is required";
      if (!selectedPublication?.edition)
        newErrors.publication = "Selected publication has no edition";
    }
    if (!defaultLanguage) newErrors.defaultLanguage = "Default language is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const editionFields = (): SurveyEditionFields | null => {
    if (newAxis) {
      if (!selectedNewAxisEdition) return null;
      return {
        editionId: selectedNewAxisEdition.editionId,
        ...(selectedNewAxisEdition.publicationId !== null
          ? { publicationId: selectedNewAxisEdition.publicationId }
          : {}),
      };
    }
    if (!selectedPublication) return null;
    return {
      edition: selectedPublication.edition,
      publication: selectedPublication.publication ?? initialValues?.publication ?? "",
      publicationId: Number(selectedPublicationId),
    };
  };

  const handleSave = async () => {
    if (flagsBlocked || !validate()) return;
    const edition = editionFields();
    if (!edition) return;

    const baseBody: CreateSurveyBody = {
      name: showNameField ? name.trim() : (initialValues?.name?.trim() ?? ""),
      ...edition,
      defaultLanguage,
      definition: initialValues?.definition,
      randomQuestionCount: initialValues?.randomQuestionCount ?? 3,
      // Empty condition arrays are omitted so a general survey's payload is
      // unchanged from the pre-conditions contract.
      ...surveyConditionsToPayloadFields(effectiveConditions),
    };

    try {
      await onSave(baseBody);
      onClose();
    } catch (error) {
      // Handled: the parent's onSave surfaced the error (toast) and the modal
      // stays open. Rethrowing from this fire-and-forget click handler would
      // only produce an unhandled promise rejection.
      console.error("Failed to save survey from SurveyFormModal", {
        error,
        edition: baseBody.edition,
        publicationId: baseBody.publicationId,
        editionId: baseBody.editionId,
      });
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  const clearError = (field: string) => {
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={title} size="medium" backdropClick={false}>
      <div className={styles.modalForm}>
        {showNameField && (
          <div className={styles.formFieldFull}>
            <Input
              label="Survey Name"
              value={name}
              onChange={e => {
                setName(e.target.value);
                clearError("name");
              }}
              fullWidth
              placeholder="Enter survey name"
              required
              error={errors.name}
            />
          </div>
        )}

        {flagsFailed ? (
          <div className={styles.formFieldFull}>
            <ErrorBanner
              error="Unable to load editions because feature flags could not be loaded"
              onRetry={retryFlags}
              loading={false}
            />
          </div>
        ) : flagsLoading ? (
          <div className={styles.formFieldFull}>
            <Select
              label="Edition"
              options={[{ value: "", label: "Loading..." }]}
              value=""
              fullWidth
              required
              disabled
            />
          </div>
        ) : newAxis ? (
          <div className={styles.formFieldFull}>
            <Select
              label="Edition"
              options={editionSelectOptions}
              value={selectedEditionId}
              onChange={e => handleNewAxisEditionChange(e.target.value)}
              fullWidth
              required
              disabled={!newAxisEditions}
              error={
                newAxisEditionsError ? "Failed to load editions. Please try again." : errors.edition
              }
            />
          </div>
        ) : (
          <div className={styles.formRow2Col}>
            <div className={styles.formField}>
              <Select
                label="Edition"
                options={editionOptions}
                value={editionYear}
                onChange={e => handleEditionChange(e.target.value)}
                fullWidth
                required
                error={errors.edition}
              />
            </div>
            <div className={styles.formField}>
              <Select
                label="Publication"
                options={publicationOptions}
                value={selectedPublicationId}
                onChange={e => handlePublicationChange(e.target.value)}
                fullWidth
                required
                disabled={!editionYear}
                error={errors.publication}
              />
            </div>
          </div>
        )}

        <div className={styles.formRow2Col}>
          <div className={styles.formField}>
            <Select
              label="Default Language"
              options={allLanguages}
              value={defaultLanguage}
              onChange={e => {
                setDefaultLanguage(e.target.value);
                clearError("defaultLanguage");
              }}
              fullWidth
              required
              error={errors.defaultLanguage}
            />
          </div>
        </div>

        <ExceptionConditionsFields
          values={conditions}
          onChange={setConditions}
          expanded={conditionsExpanded}
          onToggleExpanded={() => setConditionsExpanded(prev => !prev)}
        />

        {duplicateSurvey && <DuplicateSurveyWarning survey={duplicateSurvey} />}

        <div className={styles.modalFooter}>
          <Button variant="outlined" size="small" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleSave}
            disabled={flagsBlocked}
          >
            {submitLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export { SurveyFormModal };
